import { useAuth } from "@contexts/auth";
import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Group,
  Modal,
  Radio,
  ScrollAreaAutosize,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconPencil } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { showErrorNotification } from "@utils/showNotification";

import { imageFileTypes } from "@constants/fileTypes";

import { useUpdateProfile } from "@hooks/mutations/profile/useUpdateProfile";
import { useGetProfileByUserId } from "@hooks/queries/profile/useGetProfileByUserId";

import { StyledAddDoctorModalContent } from "../AddDoctorModal/AddDoctorModal.styled";


const laboratoryFormSchema = () =>
  z.object({
    laboratory: z.string().min(1, i18n.t("laboratory-required")),
    location: z.string().optional(),
    yearsOfService: z.string().optional(),
    bio: z.string().optional(),
    visibility: z.enum(["public", "private"]),
  });

type LaboratoryFormInputs = z.infer<ReturnType<typeof laboratoryFormSchema>>;

interface EditLaboratoryModalProps {
  opened: boolean;
  close: () => void;
  userId: string | null;
}

// File upload state interface (separate from form data)
interface FileUploads {
  photo?: File;
}

export const EditLaboratoryModal = ({ opened, close, userId }: EditLaboratoryModalProps) => {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const isFormInitialized = useRef(false);

  // File uploads state (separate from form data)
  const [fileUploads, setFileUploads] = useState<FileUploads>({
    photo: undefined,
  });

  // Mantine form with zodResolver
  const form = useForm<LaboratoryFormInputs>({
    mode: "uncontrolled",
    validate: zodResolver(laboratoryFormSchema()),
    initialValues: {
      laboratory: "",
      location: "",
      yearsOfService: "",
      bio: "",
      visibility: can("profiles.publish") ? "public" : "private",
    },
  });

  // Fetch profile data
  const { data: profileData, isLoading: isLoadingProfile } = useGetProfileByUserId({
    userId: userId || "",
    isEnabled: opened && !!userId,
  });

  // Profile update mutation
  const { mutate: updateProfile, isLoading: isUpdatingProfile } = useUpdateProfile();

  // Load profile data into form when available (only on initial load)
  useEffect(() => {
    if (profileData && !isFormInitialized.current) {
      form.setValues({
        laboratory: profileData.partnerInfo?.name || "",
        location: profileData.location || "",
        yearsOfService: profileData.yearsOfExperience?.toString() || "",
        bio: profileData.briefDescription || "",
        visibility: profileData.visibility as "public" | "private",
      });
      isFormInitialized.current = true;
    }
  }, [profileData]);

  // Reset form initialization flag when modal closes
  useEffect(() => {
    if (!opened) {
      isFormInitialized.current = false;
    }
  }, [opened]);

  // Photo upload handling
  const onPhotoDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFileUploads(prev => ({ ...prev, photo: acceptedFiles[0] }));
    }
  }, []);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } = useDropzone({
    onDrop: onPhotoDrop,
    accept: { "image/*": imageFileTypes },
    multiple: false,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleSave = (formValues: LaboratoryFormInputs) => {
    if (!profileData) {
      showErrorNotification({
        title: "error",
        message: "profile-data-not-found",
      });
      return;
    }

    setIsSaving(true);

    // Create FormData for API submission
    const updateData = new FormData();

    // Only include changed fields
    if (formValues.location !== (profileData.location || "")) {
      updateData.append("location", formValues.location || "");
    }

    if (formValues.yearsOfService !== (profileData.yearsOfExperience?.toString() || "")) {
      updateData.append("years_of_experience", formValues.yearsOfService || "");
    }

    if (formValues.bio !== (profileData.briefDescription || "")) {
      updateData.append("brief_description", formValues.bio || "");
    }

    if (formValues.visibility !== profileData.visibility) {
      updateData.append("visibility", formValues.visibility);
    }

    // Add photo if provided
    if (fileUploads.photo) {
      updateData.append("profile_image", fileUploads.photo);
    }

    updateProfile(
      {
        profileId: profileData.id,
        profileData: updateData,
      },
      {
        onSuccess: () => {
          setIsSaving(false);
          close();
        },
        onError: (error) => {
          console.error("Profile update failed:", error);
          setIsSaving(false);
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    setFileUploads({
      photo: undefined,
    });
    close();
  };

  if (isLoadingProfile) {
    return (
      <Modal
        centered
        size="md"
        title={<Text fw={500}>{t("edit-profile")}</Text>}
        opened={opened}
        onClose={handleClose}
      >
        <Text ta="center" py="xl">{t("loading")}</Text>
      </Modal>
    );
  }

  return (
    <Modal
      centered
      size="md"
      title={<Text fw={500}>{t("edit-profile")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
    >
      <StyledAddDoctorModalContent>
        <form
          onSubmit={form.onSubmit(
            (values) => handleSave(values),
            (errors) => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}
        >
          <Text className="step-title">
            {t("laboratory-info")}
          </Text>

          <Stack gap="md">
            {/* Partner's Logo */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("partners-logo")}
              </Text>
              <Group justify="center" mb="md">
                <div style={{ position: "relative" }}>
                  <Avatar
                    size={80}
                    radius="99px"
                    src={fileUploads.photo ? URL.createObjectURL(fileUploads.photo) : profileData?.profileImage}
                  />
                  <div
                    {...getPhotoRootProps()}
                    className="photo-upload-button"
                  >
                    <input {...getPhotoInputProps()} />
                    <IconPencil size={14} color="white" />
                  </div>
                </div>
              </Group>
            </div>

            {/* Laboratory Name (Read-only) */}
            <TextInput
              label={t("laboratory-actual")}
              {...form.getInputProps("laboratory")}
              key={form.key("laboratory")}
              disabled
            />

            {/* Location */}
            <TextInput
              label={t("location")}
              placeholder={t("enter-location")}
              {...form.getInputProps("location")}
              key={form.key("location")}
            />

            {/* Years of Service */}
            <TextInput
              label={t("years-of-service")}
              placeholder={t("enter-years-of-service")}
              {...form.getInputProps("yearsOfService")}
              key={form.key("yearsOfService")}
            />

            {/* Bio */}
            <Textarea
              label={t("bio")}
              placeholder={t("enter-bio")}
              rows={4}
              {...form.getInputProps("bio")}
              key={form.key("bio")}
            />

            {/* Visibility */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("visibility")}
              </Text>
              <Radio.Group
                {...form.getInputProps("visibility")}
                key={form.key("visibility")}
              >
                <Stack gap={"0.1rem"}>
                  <Radio value="public" disabled={!can("profiles.publish")} label={t("public")} />
                  <Radio value="private" disabled={!can("profiles.publish")} label={t("private")} />
                </Stack>
              </Radio.Group>
            </div>

            {/* Actions */}
            <Group justify="end" mt="xl">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                {t("cancel")}
              </Button>
              <PermissionGate permission="profiles.edit"><Button
                type="submit"
                loading={isSaving || isUpdatingProfile}
              >
                {t("save")}
              </Button></PermissionGate>
            </Group>
          </Stack>
        </form>
      </StyledAddDoctorModalContent>
    </Modal>
  );
};
